import React, { useRef, useState } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { ImageUploadModalProps } from '../../interfaces';

// IMPORTANT: You must update the ImageUploadModalProps interface where it is defined.
// The `onUpload` prop should now accept an array of strings:
//
// export interface ImageUploadModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   onUpload: (images: string[]) => Promise<void>; // <-- UPDATE THIS LINE
// }

const ImageUploadModal = ({ isOpen, onClose, onUpload }: ImageUploadModalProps) => {
  // State is now an array to hold multiple images
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to process a list of files (from selection or drop)
  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImages(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files); // Use the helper here
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  // New function to remove an image from the preview
  const handleRemoveImage = (indexToRemove: number) => {
    setUploadedImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async () => {
    if (uploadedImages.length > 0) {
      setIsUploading(true);
      try {
        await onUpload(uploadedImages); // Pass the entire array
        setUploadedImages([]); // Reset state on success
        onClose();
      } catch (error) {
        console.error('Error in multi-image upload process:', error);
        // Do not close on error, allowing user to retry
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Enhanced close handler to reset state
  const handleClose = () => {
    setUploadedImages([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="image-upload-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={handleClose}>
            <X size={20} />
          </button>

          <div className="modal-header">
            <h2>Upload Property Images</h2>
            <p>A good listing has multiple pictures. Add as many as you like!</p>
          </div>

          <div
              className={`upload-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
          >
            <Camera size={48} className="upload-icon" />
            <p>Drag & drop images here or click to browse</p>
            <span className="file-hint">You can select multiple files (JPEG, PNG)</span>
            <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                multiple // This attribute is crucial for multi-select
                onChange={handleImageChange}
                style={{ display: 'none' }}
            />
          </div>

          {/* Updated Preview Gallery with proper styling for thumbnails */}
          {uploadedImages.length > 0 && (
              <div className="image-preview-gallery">
                <div className="gallery-grid">
                  {uploadedImages.map((image, index) => (
                      <div key={index} className="image-preview-item">
                        <div className="image-container">
                          <img src={image} alt={`Preview ${index + 1}`} className="thumbnail-image" />
                          <button
                              className="remove-image-button"
                              onClick={() => handleRemoveImage(index)}
                              type="button"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                  ))}
                </div>
              </div>
          )}

          <div className="modal-actions">
            <button className="cancel-button" onClick={handleClose}>
              Cancel
            </button>
            <button
                className={`submit-button ${uploadedImages.length === 0 ? 'disabled' : ''} ${isUploading ? 'loading' : ''}`}
                onClick={handleSubmit}
                disabled={uploadedImages.length === 0 || isUploading}
            >
              {isUploading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Uploading...
                  </>
              ) : (
                  <>
                    <Upload size={16} />
                    Submit {uploadedImages.length > 0 ? `${uploadedImages.length} Image(s)`: 'Images'}
                  </>
              )}
            </button>
          </div>
        </div>

        {/* Add these styles to your CSS file or style component */}
        <style>{`
          .image-preview-gallery {
            margin: 20px 0;
            max-height: 300px;
            overflow-y: auto;
          }

          .gallery-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 12px;
            padding: 8px 0;
          }

          .image-preview-item {
            position: relative;
          }

          .image-container {
            position: relative;
            width: 100%;
            height: 120px;
            border-radius: 8px;
            overflow: hidden;
            border: 2px solid #e5e7eb;
            background-color: #f9fafb;
          }

          .thumbnail-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .remove-image-button {
            position: absolute;
            top: 4px;
            right: 4px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            border: none;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background-color 0.2s;
          }

          .remove-image-button:hover {
            background: rgba(239, 68, 68, 0.9);
          }

          .remove-image-button:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }

          /* Responsive adjustments */
          @media (max-width: 480px) {
            .gallery-grid {
              grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
              gap: 8px;
            }

            .image-container {
              height: 100px;
            }
          }
        `}</style>
      </div>
  );
};

export default ImageUploadModal;